import { UNLIMITED_CONVERSATIONS } from "@/lib/conversation-count";

export type BillingAccessState = "active" | "grace" | "suspended" | "manual_block" | "exempt";

export type BillingAccessReason =
  | "manual_block"
  | "ai_inactive"
  | "limit_reached"
  | "free_quota_withheld"
  | "billing_exempt"
  | "billing_override"
  | "prepaid_expired"
  | "overdue_expired"
  | "overdue"
  | null;

export interface BillingOverviewPlan {
  id: string;
  name: string | null;
  isCustom: boolean;
  billingMode: string | null;
  paymentStrategy: string | null;
  paymentMode: string | null;
  priceMonthly: number;
  perConversationPrice: number;
  conversationsIncluded: number;
}

export interface BillingOverviewPeriod {
  start: string;
  end: string;
  endInclusive: string;
}

export interface BillingQuotaWindow extends BillingOverviewPeriod {
  renewsOn: string;
}

export interface BillingOverviewCycle extends BillingOverviewPeriod {
  periodId: string;
  conversations: number;
  unitPrice: number;
  amount: number;
  chargeStatus: string | null;
  inReview: boolean;
  isLatestClosed: boolean;
}

export interface BillingOwedCycle {
  periodId: string;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  amountDue: number;
  settlementStatus: string | null;
}

export interface BillingOverview {
  organizationId: string;
  timezone: string;
  today: string;
  plan: BillingOverviewPlan | null;
  activePeriod: BillingOverviewPeriod | null;
  nextChargeDate: string | null;
  quotaWindow: BillingQuotaWindow | null;
  usage: { scope: "cycle" | "lifetime"; conversations: number | null; cachedCounter: number };
  limit: { included: number; extra: number; effective: number; unlimited: boolean; freeQuotaWithheld: boolean };
  estimateAmount: number | null;
  unsettledCycles: BillingOverviewCycle[];
  payableCycle: BillingOverviewCycle | null;
  payableCycles: BillingOverviewCycle[];
  payableTotal: number;
  owedCycles: BillingOwedCycle[];
  owedTotal: number;
  checkoutOpen: boolean;
  access: { state: BillingAccessState; reason: BillingAccessReason; graceUntil: string | null; overrideUntil: string | null };
}

export { UNLIMITED_CONVERSATIONS };

export const LATE_CLOSE_REVIEW = "late_close_review";

export function isCycleUnderReview(cycle: Pick<BillingOverviewCycle, "chargeStatus"> | null | undefined): boolean {
  return cycle?.chargeStatus === LATE_CLOSE_REVIEW;
}

const ACCESS_STATES: BillingAccessState[] = ["active", "grace", "suspended", "manual_block", "exempt"];

type Raw = Record<string, unknown>;

function isRecord(value: unknown): value is Raw {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function num(value: unknown, fallback = 0): number {
  const parsed = typeof value === "string" ? Number(value) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : fallback;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function parsePeriod(value: unknown): BillingOverviewPeriod | null {
  if (!isRecord(value)) return null;
  const start = str(value.start);
  const end = str(value.end);
  if (!start || !end) return null;
  return { start, end, endInclusive: str(value.end_inclusive) ?? end };
}

function parseQuotaWindow(value: unknown): BillingQuotaWindow | null {
  const period = parsePeriod(value);
  if (!period || !isRecord(value)) return null;
  return { ...period, renewsOn: str(value.renews_on) ?? period.end };
}

function parseCycle(value: unknown, isLatestFallback: boolean): BillingOverviewCycle | null {
  const period = parsePeriod(value);
  if (!period || !isRecord(value)) return null;
  return {
    ...period,
    periodId: str(value.period_id) ?? "",
    conversations: num(value.conversations),
    unitPrice: num(value.unit_price),
    amount: num(value.amount),
    chargeStatus: str(value.charge_status),
    inReview: value.in_review === true,
    isLatestClosed: typeof value.is_latest_closed === "boolean" ? value.is_latest_closed : isLatestFallback,
  };
}

function parseOwedCycle(value: unknown): BillingOwedCycle | null {
  if (!isRecord(value)) return null;
  const periodStart = str(value.period_start);
  const periodEnd = str(value.period_end);
  const dueDate = str(value.due_date);
  const amountDue = num(value.amount_due);
  if (!periodStart || !periodEnd || !dueDate || amountDue <= 0) return null;
  return {
    periodId: str(value.period_id) ?? "",
    periodStart,
    periodEnd,
    dueDate,
    amountDue,
    settlementStatus: str(value.settlement_status),
  };
}

function isPayable(cycle: BillingOverviewCycle | null): cycle is BillingOverviewCycle {
  return cycle !== null && cycle.periodId !== "" && cycle.amount > 0 && !isCycleUnderReview(cycle);
}

function parsePayableCycles(
  raw: Raw,
  payableCycle: BillingOverviewCycle | null,
): { cycles: BillingOverviewCycle[]; total: number } {
  if (!Array.isArray(raw.payable_cycles)) {
    return isPayable(payableCycle) ? { cycles: [payableCycle], total: payableCycle.amount } : { cycles: [], total: 0 };
  }
  const cycles = raw.payable_cycles
    .map((cycle) => parseCycle(cycle, false))
    .filter(isPayable)
    .sort((a, b) => a.end.localeCompare(b.end) || a.start.localeCompare(b.start));
  return { cycles, total: cycles.reduce((sum, cycle) => sum + cycle.amount, 0) };
}

export function parseBillingOverview(raw: unknown): BillingOverview | null {
  if (!isRecord(raw)) return null;
  const organizationId = str(raw.organization_id);
  if (!organizationId) return null;

  const rawPlan = isRecord(raw.plan) ? raw.plan : null;
  const planId = rawPlan ? str(rawPlan.id) : null;
  const usage = isRecord(raw.usage) ? raw.usage : {};
  const limit = isRecord(raw.limit) ? raw.limit : {};
  const access = isRecord(raw.access) ? raw.access : {};
  const state = ACCESS_STATES.includes(access.state as BillingAccessState)
    ? (access.state as BillingAccessState)
    : "active";
  const effective = num(limit.effective);
  const owedCycles = (Array.isArray(raw.owed_cycles) ? raw.owed_cycles : [])
    .map(parseOwedCycle)
    .filter((cycle): cycle is BillingOwedCycle => cycle !== null)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.periodStart.localeCompare(b.periodStart));
  const payableCycle = parseCycle(raw.payable_cycle, true);
  const payable = parsePayableCycles(raw, payableCycle);

  return {
    organizationId,
    timezone: str(raw.timezone) ?? "America/Bogota",
    today: str(raw.today) ?? "",
    plan: rawPlan && planId
      ? {
          id: planId,
          name: str(rawPlan.name),
          isCustom: rawPlan.is_custom === true,
          billingMode: str(rawPlan.billing_mode),
          paymentStrategy: str(rawPlan.payment_strategy),
          paymentMode: str(rawPlan.payment_mode),
          priceMonthly: num(rawPlan.price_monthly),
          perConversationPrice: num(rawPlan.per_conversation_price),
          conversationsIncluded: num(rawPlan.conversations_included),
        }
      : null,
    activePeriod: parsePeriod(raw.active_period),
    nextChargeDate: str(raw.next_charge_date),
    quotaWindow: parseQuotaWindow(raw.quota_window),
    usage: {
      scope: usage.scope === "lifetime" ? "lifetime" : "cycle",
      conversations: usage.conversations == null ? null : num(usage.conversations),
      cachedCounter: num(usage.cached_counter),
    },
    limit: {
      included: num(limit.included),
      extra: num(limit.extra),
      effective,
      unlimited: effective >= UNLIMITED_CONVERSATIONS,
      freeQuotaWithheld: limit.free_quota_withheld === true,
    },
    estimateAmount: raw.estimate_amount == null ? null : num(raw.estimate_amount),
    unsettledCycles: (Array.isArray(raw.unsettled_cycles) ? raw.unsettled_cycles : [])
      .map((cycle) => parseCycle(cycle, false))
      .filter((cycle): cycle is BillingOverviewCycle => cycle !== null),
    payableCycle,
    payableCycles: payable.cycles,
    payableTotal: payable.total,
    owedCycles,
    owedTotal: num(raw.owed_total),
    checkoutOpen: raw.checkout_open === true,
    access: {
      state,
      reason: (str(access.reason) as BillingAccessReason) ?? null,
      graceUntil: str(access.grace_until),
      overrideUntil: str(access.override_until),
    },
  };
}

export function parseBillingOverviews(rows: unknown): Map<string, BillingOverview> {
  const byOrg = new Map<string, BillingOverview>();
  if (!Array.isArray(rows)) return byOrg;
  for (const row of rows) {
    if (!isRecord(row)) continue;
    const overview = parseBillingOverview(row.overview);
    if (overview) byOrg.set(overview.organizationId, overview);
  }
  return byOrg;
}

export function formatConversationQuota(
  used: number | null | undefined,
  limit: number | null | undefined,
  extra: number | null | undefined,
): string {
  const usedText = (Number(used) || 0).toLocaleString("es-CO");
  const effective = (Number(limit) || 0) + (Number(extra) || 0);
  if (effective >= UNLIMITED_CONVERSATIONS) return `${usedText} / ∞`;
  return `${usedText} / ${effective.toLocaleString("es-CO")}`;
}

export function formatUsage(overview: BillingOverview): string {
  const used = (overview.usage.conversations ?? 0).toLocaleString("es-CO");
  if (overview.plan?.billingMode === "per_use") return `${used} (por consumo)`;
  if (overview.limit.unlimited) return `${used} / ∞`;
  return `${used} / ${overview.limit.effective.toLocaleString("es-CO")}`;
}

export interface OwedBalance {
  total: number;
  cycles: BillingOwedCycle[];
  oldest: BillingOwedCycle;
}

export function getOwedBalance(overview: BillingOverview | null | undefined): OwedBalance | null {
  if (!overview || overview.owedTotal <= 0 || overview.owedCycles.length === 0) return null;
  return { total: overview.owedTotal, cycles: overview.owedCycles, oldest: overview.owedCycles[0] };
}

export const CARRIED_OVER = "carried_over";

export interface CarriedOverBalance {
  total: number;
  cycles: BillingOverviewCycle[];
}

export function getCarriedOverBalance(overview: BillingOverview | null | undefined): CarriedOverBalance | null {
  if (!overview) return null;
  const owed = new Set(overview.owedCycles.map((cycle) => cycle.periodId));
  const cycles = overview.unsettledCycles.filter(
    (cycle) => cycle.chargeStatus === CARRIED_OVER && cycle.amount > 0 && !owed.has(cycle.periodId),
  );
  if (cycles.length === 0) return null;
  return { total: cycles.reduce((sum, cycle) => sum + cycle.amount, 0), cycles };
}

export function carriedOverMessage(balance: CarriedOverBalance): string {
  const amount = `$${balance.total.toLocaleString("es-CO")}`;
  return balance.cycles.length === 1
    ? `Este ciclo (${amount}) se cobra junto con el siguiente porque es menor al mínimo de Mercado Pago.`
    : `Estos ${balance.cycles.length} ciclos (${amount} en total) se cobran junto con el siguiente porque son menores al mínimo de Mercado Pago.`;
}
